<script setup>
import { defineProps } from 'vue'
import Currency from '../../components/Currency.vue'
import UserAvatar from '@/components/UserAvatar.vue'

defineProps({
  topPlayers: {
    type: Array,
    required: true,
    default: () => []
  },
  monthlyTopPlayers: {
    type: Array,
    required: true,
    default: () => []
  }
})

// Computed properties for trend handling
const getTrendIcon = (amount) => {
  return amount >= 0 ? 'mdi-trending-up' : 'mdi-trending-down'
}

const getTrendColor = (amount) => {
  return amount >= 0 ? 'success' : 'error'
}
</script>

<template>
  <v-row class="mb-6">
    <!-- Top Players All Time -->
    <v-col cols="12" md="6">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2" color="primary">mdi-podium-gold</v-icon>
          {{ $t('standings.topPlayers-overall') }}
        </v-card-title>
        <v-card-text>
          <div v-if="topPlayers.length === 0" class="text-center py-8 text-medium-emphasis">
            {{ $t('dashboard.noDataAvailable') }}
          </div>
          <v-list v-else lines="two">
            <v-list-item
              v-for="(player, index) in topPlayers"
              :key="player.id"
              class="px-0"
            >
              <template v-slot:prepend>
                <div class="d-flex align-center mr-4">
                  <v-chip
                    :color="index < 3 ? ['amber', 'blue-grey', 'orange'][index] : 'grey'"
                    size="small"
                    class="mr-2"
                  >
                    {{ player.position }}
                  </v-chip>
                  <UserAvatar :color="index < 3 ? 'primary' : 'grey'" :avatar="player.avatar" />
                </div>
              </template>
              
              <v-list-item-title class="font-weight-medium">{{ player.name }}</v-list-item-title>
              <v-list-item-subtitle>
                <Currency :amount="player.total_winnings" :class="{'text-amber': player.total_winnings < 0}" />
              </v-list-item-subtitle>
              
              <template v-slot:append>
                <v-chip
                  :color="index < 3 ? 'success' : 'default'"
                  size="small"
                  variant="tonal"
                >
                  <v-icon start size="16">mdi-star</v-icon>
                  {{  player.won_bets }}/{{ player.completed_bets }}
                </v-chip>
              </template>
            </v-list-item>
          </v-list>
        </v-card-text>

        <v-card-actions>
          <v-btn
            :to="{ name: 'standings-type', params: { type: 'overall' } }"
            variant="tonal"
            color="primary"
          >
            {{ $t('dashboard.viewFullStandings') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-col>

    <!-- Monthly Top Players -->
    <v-col cols="12" md="6">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2" color="success">mdi-calendar-month</v-icon>
          {{ $t('standings.topPlayersOfMonth', { month: $t('month.' + (new Date().getMonth() + 1)) }) }}
        </v-card-title>
        <v-card-text>
          <div v-if="monthlyTopPlayers.length === 0" class="text-center py-8 text-medium-emphasis">
            {{ $t('dashboard.noDataAvailable') }}
          </div>
          <v-list v-else lines="two">
            <v-list-item
              v-for="(player, index) in monthlyTopPlayers"
              :key="player.id"
              class="px-0"
            >
              <template v-slot:prepend>
                <div class="d-flex align-center mr-4">
                  <v-chip
                    color="success"
                    size="small"
                    class="mr-2"
                  >
                    {{ index + 1 }}
                  </v-chip>
                  <UserAvatar color="grey-darken-3" :avatar="player.name.charAt(0).toUpperCase()" />
                </div>
              </template>
              
              <v-list-item-title class="font-weight-medium">{{ player.name }}</v-list-item-title>
              <v-list-item-subtitle>
                <Currency :amount="player.total_winnings" :class="{'text-amber': player.total_winnings < 0}" />
              </v-list-item-subtitle>
              
              <template v-slot:append>
                <v-icon 
                  :color="getTrendColor(player.total_winnings)"
                  size="24"
                >
                  {{ getTrendIcon(player.total_winnings) }}
                </v-icon>
              </template>
            </v-list-item>
          </v-list>
        </v-card-text>

        <v-card-actions>
          <v-btn
            :to="{ name: 'standings-type', params: { type: 'month', timeframe: '' } }"
            variant="tonal"
            color="success"
          >
            {{ $t('dashboard.viewFullStandings') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-col>
  </v-row>
</template>

<style scoped>
.v-card {
  transition: transform 0.2s ease-in-out;
}

.v-card:hover {
  transform: translateY(-2px);
}
</style>